import type { GroupField } from "@/util/methods";

/** Which builder an asset belongs to. */
export type AssetType = "form" | "survey" | "quiz";

/** Blocking vs advisory. Errors stop the save; recommendations never do. */
export type Severity = "error" | "recommendation";

/**
 * Panel group and navigation target. Groups differ per asset type — quizzes use
 * page-based grouping and have no payments surface at all.
 */
export type BuilderSection =
  | "fields"
  | "questions"
  | "questionsPage"
  | "resultsPage"
  | "payments"
  | "logic"
  | "settings";

/** Where a finding lives, so the UI can select and reveal it. */
export interface RuleTarget {
  /** Index into `elements` (forms) or into a slide's `slideData` (surveys/quizzes). */
  elementIndex?: number;
  /** Slide holding the element. Undefined for forms. */
  slideIndex?: number;
  /** Stable element identity, used to re-find the element after a re-render. */
  elementKey?: string;
  /** Settings route to open when the finding is not tied to a canvas element. */
  settingsTab?: string;
  /** Quick Add element to highlight when the finding is about a missing one. */
  quickAddTag?: string;
  /**
   * Tab to open inside the selected element's sidebar. Findings about a field's
   * choices live under Options, not the Content tab that opens by default.
   */
  sidebarTab?: string;
}

/**
 * One finding. A rule may return several — e.g. three unlabelled fields are
 * three findings from one rule, each with its own target.
 */
export interface RuleResult {
  ruleId: string;
  severity: Severity;
  section: BuilderSection;
  /** Interpolation values for the rule's message keys, e.g. { label: "Radio 34q02" }. */
  params?: Record<string, string | number>;
  target?: RuleTarget;
  /**
   * Distinguishes findings produced by the same rule so the UI can track which
   * ones have been resolved. Defaults to the rule id when a rule yields one result.
   */
  key: string;
}

/** An element paired with where it sits, so rules never have to walk the tree. */
export interface ContextElement {
  element: GroupField;
  elementIndex: number;
  slideIndex?: number;
  elementKey: string;
}

/**
 * Immutable snapshot the rules read. Built once per run so 60+ rules share one
 * traversal, and so every rule stays a pure function of its input.
 */
export interface BuilderContext {
  assetType: AssetType;
  locationId: string;
  /** Every element across the asset, flattened, with position retained. */
  elements: ContextElement[];
  /** Slide count. 0 for forms. */
  slideCount: number;
  /** Elements grouped by slide index, for rules that reason per page. */
  slides: ContextElement[][];
  /** Slide ids, so logic rules can tell a dangling jump target from a live one. */
  slideIds: string[];
  builderConfig: Record<string, unknown>;
  formAction: Record<string, unknown>;
  payment: Record<string, unknown>;
  /**
   * Live builder product lists. `payment.products` is only written during save
   * mapping, so validation — which runs first — would read a stale array.
   */
  addedProducts: Array<Record<string, unknown>>;
  bumpProducts: Array<Record<string, unknown>>;
  /** Quiz categories. Empty for forms and surveys. */
  categories: Array<Record<string, unknown>>;
  /** Quiz results-page config, read from the saved quiz blob. */
  resultTemplate: Record<string, unknown> | null;
  emailNotificationsConfig: Record<string, unknown> | null;
  autoResponderConfig: Record<string, unknown> | null;
  opportunitySettings: Record<string, unknown> | null;
  footerStyle: Record<string, unknown>;
  /** Location custom fields, for uniqueness checks. Fetched once by the builder. */
  locationCustomFields: Array<Record<string, unknown>>;
  /** True when the location is in the Date & Time rollout allowlist. */
  dateTimeRolloutEnabled: boolean;
  /** True when the asset contains an opportunity field. */
  hasOpportunityField: boolean;
  /** True when the opportunity pipeline stage resolves. */
  pipelineStageValid: boolean;
  /** Set false by surfaces that independently disable save (e.g. footer styles). */
  saveEnabledByOtherSurfaces: boolean;
}

export interface ValidationRule {
  /** Pendo `rule_id`. Matches the PRD's section 10 names where it gives one. */
  id: string;
  severity: Severity;
  assetTypes: AssetType[];
  section: BuilderSection;
  /**
   * Skips the rule entirely when the feature it validates is not available to
   * this location — e.g. Date & Time controls outside the CfV1 rollout, whose
   * errors the user could neither see nor fix.
   */
  gate?: (ctx: BuilderContext) => boolean;
  evaluate: (ctx: BuilderContext) => RuleResult[];
}

/**
 * Highlight whichever element the Quick Add palette lists first.
 *
 * Used when the asset has no elements at all: any element is a valid place to
 * start, so the rule has no specific one to name.
 */
export const QUICK_ADD_FIRST = "__first__";
